import { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel, Stat } from "../components/Panel";
import { workspaceStatusLabel } from "../lib/adminActions";
import type { AdminWorkspaceStatus } from "../lib/adminWorkspaceStatus";
import { calculateBudgetPercentage } from "../lib/budget";
import {
  fetchAdminWorkspaceDetail,
  updateAdminWorkspaceStatus,
  type AdminWorkspaceDetailData
} from "../lib/api/adminDashboardClient";
import { buildInviteLink } from "../lib/invite";
import { calculateWorkspacePrice, formatPackageName, getRemainingDays } from "../lib/packageLimits";

function responseStatusLabel(status: string | undefined): string {
  if (status === "submitted") return "Eingereicht";
  if (status === "draft") return "Entwurf";
  return "Keine Antworten";
}

function departmentStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    invited: "Eingeladen",
    started: "Gestartet",
    submitted: "Eingereicht",
    reviewed: "Geprüft"
  };
  return labels[status] ?? status;
}

function formatAuditDetails(detailsJson: string): string {
  try {
    const parsed = JSON.parse(detailsJson) as Record<string, unknown>;
    return Object.entries(parsed)
      .map(([key, value]) => `${key}: ${String(value)}`)
      .join(" · ");
  } catch {
    return detailsJson;
  }
}

export function AdminWorkspaceDetail() {
  const requestedId = window.location.pathname.split("/").filter(Boolean).at(-1) ?? "";
  const [detail, setDetail] = useState<AdminWorkspaceDetailData | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [actionError, setActionError] = useState<string | undefined>();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const loadDetail = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const nextDetail = await fetchAdminWorkspaceDetail(requestedId);
      setDetail(nextDetail);
    } catch (caught) {
      setDetail(undefined);
      setError(caught instanceof Error ? caught.message : "Workspace nicht gefunden.");
    } finally {
      setIsLoading(false);
    }
  }, [requestedId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const budgetPercentage = useMemo(() => {
    if (!detail?.usage) return 0;
    return calculateBudgetPercentage(detail.usage.techCostEstimateEur, detail.usage.techBudgetLimitEur);
  }, [detail]);

  async function handleStatusChange(status: AdminWorkspaceStatus) {
    if (!detail) return;
    setIsUpdatingStatus(true);
    setNotice(undefined);
    setActionError(undefined);
    try {
      const updatedWorkspace = await updateAdminWorkspaceStatus(detail.workspace.id, status);
      setNotice(`Workspace wurde auf ${workspaceStatusLabel(updatedWorkspace.status)} gesetzt.`);
      await loadDetail();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : "Status konnte nicht aktualisiert werden.");
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  function disabledAction() {
    setNotice("Diese Admin-Aktion ist noch nicht Teil dieses Tickets und bleibt deaktiviert, damit keine lokalen Demo-Daten verändert werden.");
  }

  if (isLoading) {
    return (
      <Layout
        eyebrow="Workspace Detail"
        title="Workspace wird geladen"
        description="HAUFFE lädt echte D1-Daten für diesen Workspace."
      >
        <Panel title="Laden" variant="soft">
          <p>Workspace-Details werden geladen...</p>
        </Panel>
      </Layout>
    );
  }

  if (error || !detail) {
    return (
      <Layout
        eyebrow="Workspace Detail"
        title="Workspace nicht gefunden."
        description="Für diese Workspace-ID wurden keine D1-Daten gefunden."
      >
        <Panel title="Hinweis" variant="soft">
          <p className="form-error" role="alert">{error ?? "Workspace nicht gefunden."}</p>
          <a className="button button-primary" href="/admin">
            Zur Admin-Übersicht
          </a>
        </Panel>
      </Layout>
    );
  }

  const { workspace, organization, owners, departments, usage, latestSummary, auditLogs } = detail;
  const submittedCount = departments.filter((department) => department.responseStatus === "submitted").length;
  const price = calculateWorkspacePrice(workspace.packageType, workspace.maxDepartments);

  return (
    <Layout
      eyebrow="Workspace Detail"
      title={workspace.name}
      description="Status, Paket, Laufzeit, Abteilungen, Company Owner und interne technische Nutzung aus D1."
    >
      <div className="grid grid-4">
        <Stat label="Status" value={workspaceStatusLabel(workspace.status)} />
        <Stat label="Paket" value={formatPackageName(workspace.packageType)} />
        <Stat label="Preis" value={`${price.toLocaleString("de-DE")} EUR netto`} />
        <Stat label="Laufzeit" value={`${workspace.startDate} – ${workspace.endDate}`} />
        <Stat label="Verbleibende Tage" value={String(getRemainingDays(workspace.endDate))} />
        <Stat label="Eingereicht" value={`${submittedCount}/${departments.length}`} />
        <Stat label="Interne Nutzung" value={`${budgetPercentage.toFixed(0)}%`} />
      </div>

      {notice ? (
        <Panel title="Hinweis" variant="soft">
          <p>{notice}</p>
        </Panel>
      ) : null}

      <Panel title="Workspace verwalten" variant="accent">
        <p className="form-note">
          Statusaktionen werden direkt in D1 gespeichert und im Audit Log dokumentiert. Paket-, Laufzeit-,
          Owner- und Invite-Aktionen bleiben deaktiviert, bis sie ebenfalls D1-basiert umgesetzt sind.
        </p>
        <div className="admin-actions">
          <button
            className="button button-secondary"
            type="button"
            disabled={isUpdatingStatus || workspace.status === "active"}
            onClick={() => void handleStatusChange("active")}
          >
            Aktivieren
          </button>
          <button
            className="button button-secondary"
            type="button"
            disabled={isUpdatingStatus || workspace.status === "paused"}
            onClick={() => void handleStatusChange("paused")}
          >
            Pausieren
          </button>
          <button
            className="button button-secondary"
            type="button"
            disabled={isUpdatingStatus || workspace.status === "locked"}
            onClick={() => void handleStatusChange("locked")}
          >
            Sperren
          </button>
          <button
            className="button button-secondary"
            type="button"
            disabled={isUpdatingStatus || workspace.status === "archived"}
            onClick={() => void handleStatusChange("archived")}
          >
            Archivieren
          </button>
          <button
            className="button button-secondary"
            type="button"
            disabled={isUpdatingStatus || workspace.status === "draft"}
            onClick={() => void handleStatusChange("draft")}
          >
            Zurück auf Draft
          </button>
          <button className="button button-secondary" type="button" onClick={disabledAction}>
            Verlängern
          </button>
          <button className="button button-secondary" type="button" onClick={disabledAction}>
            Paket ändern
          </button>
          <button className="button button-secondary" type="button" onClick={disabledAction}>
            Abteilungslimit ändern
          </button>
          <button className="button button-secondary" type="button" onClick={disabledAction}>
            Company Owner ändern
          </button>
        </div>
        {actionError ? <p className="form-error" role="alert">{actionError}</p> : null}
      </Panel>

      <div className="grid grid-2">
        <Panel title="Organization">
          <p>{organization.name}</p>
          <p className="muted">
            {[organization.industry, organization.companySize].filter(Boolean).join(" · ") || "Keine weiteren Angaben"}
          </p>
          <p>Fokus: {workspace.focusType}</p>
          <p>Ziel: {workspace.goal ?? "Kein Ziel hinterlegt."}</p>
        </Panel>
        <Panel title="Company Owner">
          {owners.length === 0 ? (
            <p>Noch kein Company Owner zugeordnet.</p>
          ) : (
            <div className="data-list">
              {owners.map((owner) => (
                <div key={owner.id}>
                  <span>{owner.name ?? "Kein Name hinterlegt"}</span>
                  <strong>{owner.email}</strong>
                  <span>{owner.isActive ? "Aktiv" : "Inaktiv"}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel title="Interne technische Nutzung" variant="accent">
          <div className="budget-bar" aria-label={`Interne technische Nutzung ${budgetPercentage.toFixed(0)} Prozent`}>
            <span style={{ width: `${Math.min(100, budgetPercentage)}%` }} />
          </div>
          <p>
            {(usage?.techCostEstimateEur ?? 0).toFixed(2)} EUR von{" "}
            {(usage?.techBudgetLimitEur ?? 50).toFixed(2)} EUR internem Techkostenlimit genutzt.
          </p>
          <p className="muted">Diese Werte stammen aus D1 und sind nur für HAUFFE Admins sichtbar.</p>
        </Panel>
      </div>

      <Panel title="Workspace Usage intern" variant="accent">
        <div className="data-list">
          <div>
            <span>AI input tokens</span>
            <strong>{usage?.aiInputTokensUsed ?? 0}</strong>
          </div>
          <div>
            <span>AI output tokens</span>
            <strong>{usage?.aiOutputTokensUsed ?? 0}</strong>
          </div>
          <div>
            <span>AI calls</span>
            <strong>{usage?.aiCallsUsed ?? 0}</strong>
          </div>
          <div>
            <span>AI cost estimate</span>
            <strong>{(usage?.aiCostEstimateEur ?? 0).toFixed(4)} EUR</strong>
          </div>
          <div>
            <span>Cloudflare cost estimate</span>
            <strong>{(usage?.cloudflareCostEstimateEur ?? 0).toFixed(2)} EUR</strong>
          </div>
          <div>
            <span>Tech cost estimate</span>
            <strong>{(usage?.techCostEstimateEur ?? 0).toFixed(2)} EUR</strong>
          </div>
          <div>
            <span>Tech budget limit</span>
            <strong>{(usage?.techBudgetLimitEur ?? 50).toFixed(2)} EUR</strong>
          </div>
          <div>
            <span>Usage status</span>
            <strong>{usage?.usageStatus ?? "active"}</strong>
          </div>
        </div>
      </Panel>

      <Panel title="AI Summary Status" variant="accent">
        <div className="data-list">
          <div>
            <span>Letzte AI-Zusammenfassung</span>
            <strong>{latestSummary ? latestSummary.createdAt : "Noch nicht erzeugt"}</strong>
          </div>
          <div>
            <span>Modell</span>
            <strong>{latestSummary ? latestSummary.model : "Noch nicht genutzt"}</strong>
          </div>
        </div>
      </Panel>

      <Panel title="Abteilungen">
        {departments.length === 0 ? (
          <p>Noch keine Abteilungen vorhanden.</p>
        ) : (
          <div className="table-like admin-department-table">
            {departments.map((department) => {
              const inviteLink = buildInviteLink(window.location.origin, department.inviteToken);
              return (
                <div key={department.id}>
                  <strong>{department.name}</strong>
                  <span>{department.participantEmail ?? "Kein Contributor zugeordnet"}</span>
                  <span className={`status-badge status-${department.status}`}>{departmentStatusLabel(department.status)}</span>
                  <span>{inviteLink}</span>
                  <span>{responseStatusLabel(department.responseStatus)}</span>
                  <span>{department.submittedAt ? new Date(department.submittedAt).toLocaleString("de-DE") : "Noch nicht eingereicht"}</span>
                  <a className="button button-secondary" href={`/admin/workspaces/${workspace.id}/departments/${department.id}/responses`}>
                    Antworten ansehen
                  </a>
                  <button className="button button-secondary" type="button" onClick={disabledAction}>
                    Abteilung erneut öffnen
                  </button>
                  <button className="button button-secondary" type="button" onClick={disabledAction}>
                    Invite-Link neu erzeugen
                  </button>
                  <span>{department.responseStatus ? "Antwortstatus aus D1 vorhanden" : "Für diese Abteilung liegen noch keine Antworten vor."}</span>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <Panel title="Admin-Aktivitäten" variant="accent">
        {auditLogs.length === 0 ? (
          <p>Noch keine Admin-Aktion protokolliert.</p>
        ) : (
          <div className="data-list audit-log-list">
            {auditLogs.map((log) => (
              <div key={log.id}>
                <span>{new Date(log.createdAt).toLocaleString("de-DE")}</span>
                <strong>{log.action}</strong>
                <span>{formatAuditDetails(log.detailsJson)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </Layout>
  );
}
